import ApiService from "./api-service";
import { BillingProvider, Modules } from "../util/constants";

const noop = async () => {};

const addonFragment = /* GraphQL */ `
  fragment AvailableAddonFlatFeeParts on AvailableAddonFlatFee {
    chargebeeAddonId
    description
    id
    includedQuantity
    name
    price
    quantity
    type
    period
    currency
  }

  fragment AvailableAddonVolumeParts on AvailableAddonVolume {
    chargebeeAddonId
    description
    id
    includedQuantity
    name
    quantity
    tiers {
      price
      startingUnit
      endingUnit
    }
    quantityStep
    type
    period
    currency
  }

  fragment AvailableAddonPerUnitParts on AvailableAddonPerUnit {
    chargebeeAddonId
    description
    id
    includedQuantity
    name
    price
    quantity
    quantityStep
    type
    period
    currency
  }
`;

const SubscriptionService = {
  async setSubscriberId({ shortCode, subscriberId }) {
    const body = {
      query: `mutation($subscriberId: String!) {
        updateSubscriptionSubscriberId(subscriberId: $subscriberId)
      }`,
      variables: { subscriberId },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.updateSubscriptionSubscriberId;
  },
  async getActiveXeroSubscription({ shortCode, subscriptionId }) {
    const data = await ApiService.orgEndpoint(shortCode).get(
      `/subscription/xero/${subscriptionId}`,
    );

    const subscriptionItem = data.subscriptionItems.find(
      (s) => s.status === "ACTIVE",
    );
    return subscriptionItem?.plan;
  },
  async getSubscribingUsersInfo(ShortCode) {
    const currentUser = {
      query: `query {
        user {
          _id
        }
      }
      `,
    };

    const createdBy = {
      query: `query {
        organisation {
          createdBy {
            _id
          }
        }
      }
      `,
    };

    const userInfo = await ApiService.meEndpoint().post("/", currentUser);
    const owner = await ApiService.orgEndpoint(ShortCode).post("/", createdBy);
    return {
      createdBy: userInfo.user._id,
      currentUser: owner.organisation.createdBy._id,
    };
  },
  getActiveSubscription(service) {
    switch (service) {
      case BillingProvider.XERO:
        return SubscriptionService.getActiveXeroSubscription;
      default:
        // chargebee pulled from mongodb
        return noop;
    }
  },
  async getDowngradeSubscriptionOffers({ ShortCode, planName }) {
    const body = {
      query: `query($planName: String!) {
          organisation {
            featureOveragePriceIfDowngrade(planName: $planName) {
              users
              invoices
              domainVerification
            }
          }
        }
      `,
      variables: {
        planName,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },

  async addAddOnToSubscription({
    shortCode,
    addonId,
    moduleName,
    quantity,
    proratedQuantity,
  }) {
    const body = {
      query: /* GraphQL */ `
        mutation (
          $addonId: String
          $moduleName: String
          $quantity: Int
          $proratedQuantity: Int
        ) {
          addAddOnToSubscription(
            addonId: $addonId
            moduleName: $moduleName
            quantity: $quantity
            proratedQuantity: $proratedQuantity
          ) {
            addon {
              id
              quantity
              type
            }
          }
        }
      `,
      variables: { addonId, moduleName, quantity, proratedQuantity },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.addAddOnToSubscription;
  },
  async removeAddOn({ shortCode, addonId, moduleName, moduleNames }) {
    const body = {
      query: /* GraphQL */ `
        mutation (
          $addonId: String
          $moduleName: String
          $moduleNames: [String!]
        ) {
          removeAddOn(
            addonId: $addonId
            moduleName: $moduleName
            moduleNames: $moduleNames
          )
        }
      `,
      variables: { addonId, moduleName, moduleNames },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.removeAddOn;
  },
  async cancelScheduledChange({ shortCode, addonId, moduleName, moduleNames }) {
    const body = {
      query: /* GraphQL */ `
        mutation (
          $addonId: String
          $moduleName: String
          $moduleNames: [String!]
        ) {
          cancelScheduledChange(
            addonId: $addonId
            moduleName: $moduleName
            moduleNames: $moduleNames
          )
        }
      `,
      variables: { addonId, moduleName, moduleNames },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.cancelScheduledChange;
  },
  async getAvailableAddons({ shortCode, moduleName = Modules.RECEIVABLES }) {
    const body = {
      query: /* GraphQL */ `
        ${addonFragment}
        query ($moduleName: String) {
          organisation {
            availableAddons(moduleName: $moduleName) {
              ...AvailableAddonFlatFeeParts
              ...AvailableAddonVolumeParts
              ...AvailableAddonPerUnitParts
            }
          }
        }
      `,
      variables: { moduleName },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.organisation.availableAddons;
  },
  async getAddon({ shortCode, addonId, moduleName }) {
    const body = {
      query: /* GraphQL */ `
        ${addonFragment}
        query ($addonId: String!, $moduleName: String) {
          organisation {
            getAddon(addonId: $addonId, moduleName: $moduleName) {
              ...AvailableAddonFlatFeeParts
              ...AvailableAddonVolumeParts
              ...AvailableAddonPerUnitParts
            }
          }
        }
      `,
      variables: { addonId, moduleName },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.organisation.getAddon;
  },
  async estimateAddon({ shortCode, id, quantity, moduleName }) {
    const body = {
      query: /* GraphQL */ `
        query ($id: String!, $quantity: Int!, $moduleName: String) {
          organisation {
            estimateAddon(
              id: $id
              quantity: $quantity
              moduleName: $moduleName
            ) {
              proratedQuantity
              proratedAmount
              currency
            }
          }
        }
      `,
      variables: { id, quantity, moduleName },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.organisation.estimateAddon;
  },

  async startModuleSubscriptionTrial({
    shortCode,
    moduleName,
    planName,
    currency,
    period,
  }) {
    const body = {
      query: /* GraphQL */ `
        mutation (
          $moduleName: String!
          $planName: String!
          $currency: String!
          $period: String
        ) {
          startModuleSubscriptionTrial(
            moduleName: $moduleName
            planName: $planName
            currency: $currency
            period: $period
          )
        }
      `,
      variables: { moduleName, planName, currency, period },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.startModuleSubscriptionTrial;
  },
};

export default SubscriptionService;
